import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { RewardFromCodeFailed } from '../components/popups/reward'
import { changeRewardsState, resetRewardsState } from '../store/actions/rewards'
import { REWARDS_PROFILE_DISPATCH } from '../store/reducers/rewards/rewards'
import { EXCHANGE_POINTS_URL, SHOP_URL } from '../constants/navigation'
import {
  REWARD_POINTS_PROCESS_STEP,
  DEFAULT_CONTAINER_STATE,
  REWARD_NFT_PROCESS_STEP
} from '../constants/containerStates'
import { useReceiveReward } from '../hooks/useReceiveReward'
import { trackGTMDataLayer } from 'wrappers/trackGTMDataLayer'
import Overview from '../components/overview/Overview'
import { resetPreviousUserPoints } from '../store/actions/account'
import NftCodeSuccess from '../components/popups/nft/NftCodeSuccess'
import ReceivePointsSuccess from '../components/popups/reward/ReceivePointsSuccess'
import { SHOP_URL_FILTER_TYPE } from '../util/shop.helpers'

const OverviewContainer = ({
  changeRewardsState,
  rewardsProfile,
  tokenProfile,
  accountProfile,
  mainProfile,
  resetPreviousUserPoints,
  resetRewardsState,
  isQuestsEnabled
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  useEffect(() => {
    trackGTMDataLayer(location.pathname)
  }, [])

  const [popupState, setPopupState] = useState(DEFAULT_CONTAINER_STATE.NONE)
  const [receiveRewardCard, renderRewardPopup, rewardPopupState] =
    useReceiveReward({ t, rewardsProfile, tokenProfile })

  useEffect(() => {
    setPopupState(rewardPopupState)
  }, [rewardPopupState])

  useEffect(() => {
    return () => {
      changeRewardsState(REWARDS_PROFILE_DISPATCH.ERROR, {})
      resetCodeState()
      resetPreviousUserPoints()
      resetRewardsState()
    }
  }, [changeRewardsState])

  useEffect(() => {
    if (rewardsProfile.codeValidityState) {
      setPopupState(rewardsProfile.codeValidityState)
    }
  }, [rewardsProfile.codeValidityState])

  const handleNoPopup = () => {
    resetRewardCode()
    changeRewardsState(REWARDS_PROFILE_DISPATCH.ERROR, {})
    setPopupState(DEFAULT_CONTAINER_STATE.NONE)
  }

  const resetCodeState = () => {
    changeRewardsState(
      REWARDS_PROFILE_DISPATCH.CODE_VALIDITY_STATE,
      DEFAULT_CONTAINER_STATE.NONE
    )
  }

  const resetRewardCode = () => {
    changeRewardsState(REWARDS_PROFILE_DISPATCH.REWARD_CODE, '')
  }

  const handlePopupSubmit = () => {
    switch (popupState) {
      case REWARD_POINTS_PROCESS_STEP.REWARD_FROM_CODE_FAILED:
        handleNoPopup()
        break

      case REWARD_NFT_PROCESS_STEP.NFT_RECEIVED:
        resetRewardCode()
        resetCodeState()
        handleNoPopup()
        if (!rewardsProfile.reward?.campaign?.deltaSeconds) {
          navigate(SHOP_URL, {
            state: { action: SHOP_URL_FILTER_TYPE.MY_ITEMS }
          })
        }
        break

      case REWARD_POINTS_PROCESS_STEP.POINTS_RECEIVED:
        handleNoPopup()
        if (!rewardsProfile.reward?.campaign?.deltaSeconds) {
          navigate(EXCHANGE_POINTS_URL)
        }
        break

      default:
        break
    }
  }

  const handlePopupCancel = () => {
    switch (popupState) {
      case REWARD_POINTS_PROCESS_STEP.POINTS_RECEIVED:
        handleNoPopup()
        break

      default:
        break
    }
  }

  const renderPopup = () => {
    const { reward } = rewardsProfile
    switch (popupState) {
      case REWARD_POINTS_PROCESS_STEP.REWARD_FROM_CODE_FAILED:
        return (
          <RewardFromCodeFailed
            onSubmit={handlePopupSubmit}
            tokenName={tokenProfile.name}
            t={t}
          />
        )

      case REWARD_POINTS_PROCESS_STEP.POINTS_RECEIVED:
        return (
          <ReceivePointsSuccess
            reward={reward}
            onSubmit={handlePopupSubmit}
            tokenProfile={tokenProfile}
            t={t}
            onCancel={handlePopupCancel}
          />
        )

      case REWARD_NFT_PROCESS_STEP.NFT_RECEIVED:
        return (
          <NftCodeSuccess
            onAwesomeClick={handlePopupSubmit}
            reward={reward}
            t={t}
          />
        )

      default:
        break
    }
  }

  return (
    <>
      <Overview
        t={t}
        symbol={tokenProfile.symbol}
        firstName={accountProfile.firstName}
        receiveRewardCard={receiveRewardCard}
        mainProfile={mainProfile}
        isQuestsEnabled={isQuestsEnabled}
      />
      {renderPopup()}
      {renderRewardPopup()}
    </>
  )
}

const mapStateToProps = ({ mainReducer, rewardsReducer, accountReducer }) => {
  const { tokenProfile, mainProfile } = mainReducer
  const { rewardsProfile } = rewardsReducer
  const { accountProfile } = accountReducer
  return {
    rewardsProfile,
    tokenProfile,
    accountProfile,
    mainProfile
  }
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      changeRewardsState,
      resetPreviousUserPoints,
      resetRewardsState
    },
    dispatch
  )

export default connect(mapStateToProps, mapDispatchToProps)(OverviewContainer)
